import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ApiResponse, Lesson, LessonStatus, LessonType} from '../../types/portal';
import { apiService } from '../../services/api';

// API functions for lessons
const getLessons = async (filter?: string, from?: string, to?: string, type?: string, teacherId?: string, studentId?: string, licenseType?: string): Promise<ApiResponse<Lesson[]>> => {
  try {
    let url = "/super-admin/api/all-lessons/page?size=1000";
    if (filter && filter !== 'all') url += `&filter=${filter}`;
    if (from) url += `&from=${from}`;
    if (to) url += `&to=${to}`;
    if (type) url += `&lessonType=${type}`;
    if (licenseType) url += `&licenseType=${licenseType}`;
    if (teacherId && teacherId !== 'all') url += `&teacherId=${teacherId}`;
    if (studentId && studentId !== 'all') url += `&studentId=${studentId}`;
    
    const resp = await apiService.get<any>(url);
    const lessons = Array.isArray(resp?.data?.lessons) ? resp.data.lessons : Array.isArray(resp?.lessons) ? resp.lessons : Array.isArray(resp?.data?.content) ? resp.data.content : Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
    return { success: true, data: lessons };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch lessons' };
  }
};

const getStudentLessons = async (studentId: string, filter?: string, from?: string, to?: string, type?: string, licenseType?: string): Promise<ApiResponse<Lesson[]>> => {
  try {
    // Note: The backend might not yet support all these params for my-lessons, 
    // but we'll add them to the URL in case it does or will.
    let url = `/api/lessons/my-lessons?size=1000`;
    if (filter && filter !== 'all') url += `&filter=${filter}`;
    if (from) url += `&from=${from}`;
    if (to) url += `&to=${to}`;
    if (type) url += `&lessonType=${type}`;
    if (licenseType) url += `&licenseType=${licenseType}`;

    const resp = await apiService.get<any>(url);
    const lessons = Array.isArray(resp) ? resp : (resp?.data ?? resp);
    return { success: true, data: lessons as Lesson[] };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch student lessons' };
  }
};

const getTeacherLessons = async (teacherId: string, filter?: string, from?: string, to?: string, type?: string, studentId?: string, licenseType?: string): Promise<ApiResponse<Lesson[]>> => {
  try {
    let url = `/api/lessons/my-teaching/page?size=1000`;
    if (filter && filter !== 'all') url += `&filter=${filter}`;
    if (from) url += `&from=${from}`;
    if (to) url += `&to=${to}`;
    if (type) url += `&lessonType=${type}`;
    if (studentId && studentId !== 'all') url += `&studentId=${studentId}`;
    if (licenseType) url += `&licenseType=${licenseType}`;

    const resp = await apiService.get<any>(url);
    const lessons = Array.isArray(resp?.data?.lessons) ? resp.data.lessons : Array.isArray(resp?.lessons) ? resp.lessons : Array.isArray(resp?.data?.content) ? resp.data.content : Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
    return { success: true, data: lessons };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch teacher lessons' };
  }
};

const createLessonApi = async (lessonData: Omit<Lesson, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Lesson>> => {
  return await apiService.post<ApiResponse<Lesson>>("/api/lessons/create", lessonData);
};

const updateLessonStatusApi = async (lessonId: string, status: LessonStatus): Promise<ApiResponse<void>> => {
  return await apiService.put<ApiResponse<void>>(`/super-admin/api/all-lessons/${lessonId}/status`, { status });
};



interface LessonState {
  lessons: Lesson[];
  studentLessons: Lesson[]; // for students: currently used for available or all depending on fetch
  studentPastLessons: Lesson[]; // past completed lessons for student
  studentStats: { totalCompleted: number } | null;
  teacherLessons: Lesson[];
  isLoading: boolean;
  error: string | null;
}


// Async thunks

export const fetchLessons = createAsyncThunk(
  'lesson/fetchLessons',
  async (params: { filter?: string; from?: string; to?: string; type?: string; teacherId?: string; studentId?: string; licenseType?: string } | void, { rejectWithValue }) => {
    try {
      const { filter, from, to, type, teacherId, studentId, licenseType } = params || {};
      const response = await getLessons(filter, from, to, type, teacherId, studentId, licenseType);
      let lessonsArray;
      if (Array.isArray(response)) {
        lessonsArray = response;
      } else if (response.success && Array.isArray(response.data)) {
        lessonsArray = response.data;
      } else {
        // If response.error contains HTML, show a clear message
        if (response.error && typeof response.error === 'string' && response.error.includes('<html')) {
          return rejectWithValue('Authentication failed or endpoint misconfigured. Received HTML instead of JSON.');
        }
        return rejectWithValue(response.error || 'Failed to fetch lessons');
      }
      const mappedLessons = lessonsArray.map((lesson: any) => ({
        id: String(lesson.id),
        studentId: String(lesson.studentId ?? lesson.student_id ?? lesson.student?.id ?? ''),
        teacherId: String(lesson.teacherId ?? lesson.teacher_id ?? lesson.teacher?.id ?? ''),
        dateTime: lesson.dateTime ?? lesson.lessonDateTime ?? lesson.startDateTime ?? lesson.startTime ?? lesson.start ?? lesson.start_time,
        duration: Number(lesson.duration ?? lesson.durationMinutes ?? 60),
        status: String(lesson.status ?? (lesson.isBooked ? 'SCHEDULED' : 'AVAILABLE')).toUpperCase(),
        type: lesson.type ?? lesson.lessonType ?? 'practical',
        location: lesson.location,
        // Include display names when backend provides them for better UI labels
        studentName: lesson.studentName ?? lesson.student_full_name ?? lesson.studentFullName ?? (lesson.student ? ((lesson.student.firstName || '') + ' ' + (lesson.student.lastName || '')).trim() : null),
        teacherName: lesson.teacherName ?? lesson.teacher_full_name ?? lesson.teacherFullName ?? (lesson.teacher ? ((lesson.teacher.firstName || '') + ' ' + (lesson.teacher.lastName || '')).trim() : null),
        notes: lesson.notes,
        teacherNote: lesson.teacherNote,
        studentNote: lesson.studentNote,
        examType: lesson.examType,
        licenseType: lesson.licenseType,
        examResult: lesson.examResult,
        createdAt: lesson.createdAt,
        updatedAt: lesson.updatedAt,
      }));
      return mappedLessons;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Network error occurred');
    }
  }
);

export const fetchStudentLessons = createAsyncThunk(
  'lesson/fetchStudentLessons',
  async (params: { studentId: string; filter?: string; from?: string; to?: string; type?: string; licenseType?: string }, { rejectWithValue }) => {
    try {
      const { studentId, filter, from, to, type, licenseType } = params;
      const response = await getStudentLessons(studentId, filter, from, to, type, licenseType);
      let lessonsArray;
      if (Array.isArray(response)) {
        lessonsArray = response;
      } else if (response.success && Array.isArray(response.data)) {
        lessonsArray = response.data;
      } else {
        return rejectWithValue(response.error || 'Failed to fetch student lessons');
      }
      return lessonsArray;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Network error occurred');
    }
  }
);

export const fetchTeacherLessons = createAsyncThunk(
  'lesson/fetchTeacherLessons',
  async (params: { teacherId: string; filter?: string; from?: string; to?: string; type?: string; studentId?: string; licenseType?: string }, { rejectWithValue }) => {
    try {
      const { teacherId, filter, from, to, type, studentId, licenseType } = params;
      const response = await getTeacherLessons(teacherId, filter, from, to, type, studentId, licenseType);
      let lessonsArray;
      if (Array.isArray(response)) {
        lessonsArray = response;
      } else if (response.success && Array.isArray(response.data)) {
        lessonsArray = response.data;
      } else {
        return rejectWithValue(response.error || 'Failed to fetch teacher lessons');
      }
      return lessonsArray;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Network error occurred');
    }
  }
);

export const createLesson = createAsyncThunk(
  'lesson/createLesson',
  async (lessonData: Omit<Lesson, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const response = await createLessonApi(lessonData);
      if (response.success && response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.message || response.error || 'Failed to create lesson');
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Network error occurred');
    }
  }
);

export const createBulkLessons = createAsyncThunk(
  'lesson/createBulkLessons',
  async (params: { 
    startDate: string; 
    endDate: string; 
    intervalHours: number; 
    durationMinutes: number;
    lessonType?: string;
    examType?: string;
    licenseType?: string;
  }, { rejectWithValue }) => {
    try {
      const { startDate, endDate, intervalHours, durationMinutes, lessonType, examType, licenseType } = params;
      let url = `/api/lessons/bulk-create?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&intervalHours=${intervalHours}&durationMinutes=${durationMinutes}`;
      
      if (lessonType) url += `&lessonType=${encodeURIComponent(lessonType)}`;
      if (examType) url += `&examType=${encodeURIComponent(examType)}`;
      if (licenseType) url += `&licenseType=${encodeURIComponent(licenseType)}`;

      const resp = await apiService.post<any>(url);
      const lessons = Array.isArray(resp) ? resp : (resp?.data ?? resp);
      return lessons as Lesson[];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create bulk lessons');
    }
  }
);

export const updateLessonStatus = createAsyncThunk(
  'lesson/updateLessonStatus',
  async ({ lessonId, status }: { lessonId: string; status: LessonStatus }, { rejectWithValue }) => {
    try {
      const response = await updateLessonStatusApi(lessonId, status);
      if (response.success) {
        return { lessonId, status };
      } else {
        return rejectWithValue(response.error || 'Failed to update lesson status');
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Network error occurred');
    }
  }
);

// Initial state
const initialState: LessonState = {
  lessons: [],
  studentLessons: [],
  studentPastLessons: [],
  studentStats: null,
  teacherLessons: [],
  isLoading: false,
  error: null,
};

// Lesson slice
const lessonSlice = createSlice({
  name: 'lesson',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch lessons cases
    builder
      .addCase(fetchLessons.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLessons.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lessons = action.payload;
        state.error = null;
      })
      .addCase(fetchLessons.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch student lessons cases
    builder
      .addCase(fetchStudentLessons.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudentLessons.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studentLessons = action.payload;
        state.error = null;
      })
      .addCase(fetchStudentLessons.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch teacher lessons cases
    builder
      .addCase(fetchTeacherLessons.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTeacherLessons.fulfilled, (state, action) => {
        state.isLoading = false;
        state.teacherLessons = action.payload;
        state.error = null;
      })
      .addCase(fetchTeacherLessons.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create lesson cases
    builder
      .addCase(createLesson.fulfilled, (state, action) => {
        state.lessons.push(action.payload);
      });

    // Update lesson status cases
    builder
      .addCase(updateLessonStatus.fulfilled, (state, action) => {
        const { lessonId, status } = action.payload;
        
        // Update in main lessons array
        const lesson = state.lessons.find(l => l.id === lessonId);
        if (lesson) {
          lesson.status = status;
          lesson.updatedAt = new Date().toISOString();
        }
        
        // Update in student lessons array
        const studentLesson = state.studentLessons.find(l => l.id === lessonId);
        if (studentLesson) {
          studentLesson.status = status;
          studentLesson.updatedAt = new Date().toISOString();
        }
        
        // Update in teacher lessons array
        const teacherLesson = state.teacherLessons.find(l => l.id === lessonId);
        if (teacherLesson) {
          teacherLesson.status = status;
          teacherLesson.updatedAt = new Date().toISOString();
        }
      });
  },
});

export const { clearError } = lessonSlice.actions;
export default lessonSlice.reducer;
